/**
 * Chromium and Edge back a composited layer with a single GPU texture and drop the layer when it
 * exceeds the driver's max texture size, which renders large scans as a blank, flickering box.
 * Desktop GPUs report at least 8192 (D3D 10-class hardware; D3D 11 and Metal report 16384), so
 * bitmaps handed to the file viewer stay at half that. 4096 still covers a fitted page at the
 * viewer's 3× maximum zoom on a 2× screen.
 */
export const MAX_PREVIEW_BITMAP_DIMENSION = 4096

// A PDF page is redrawn only when the zoom passes the next half step, which caps the number of
// redraws (each one briefly blanks the canvas) at four on the way up to the 3× maximum.
const PDF_RENDER_SCALE_STEP = 0.5

interface PdfCanvasPixelRatioParams {
  screenPixelRatio: number
  renderScale: number
  cssWidth: number
  cssHeight: number
}

export const getPdfRenderScaleStep = (zoomScale: number) => {
  return Math.max(1, Math.ceil(zoomScale / PDF_RENDER_SCALE_STEP) * PDF_RENDER_SCALE_STEP)
}

/**
 * The pixel ratio react-pdf should draw a page's canvas at: enough device pixels for the current
 * zoom, held under the bitmap cap, and never coarser than an unzoomed page on this screen. Until
 * the page size is known the cap cannot be checked, so the page is drawn at the screen ratio.
 */
export const getPdfCanvasPixelRatio = ({
  screenPixelRatio,
  renderScale,
  cssWidth,
  cssHeight,
}: PdfCanvasPixelRatioParams) => {
  const requestedPixelRatio = screenPixelRatio * renderScale
  const longestCssEdge = Math.max(cssWidth, cssHeight)

  if (longestCssEdge <= 0) {
    return screenPixelRatio
  }

  const cappedPixelRatio = MAX_PREVIEW_BITMAP_DIMENSION / longestCssEdge

  return Math.min(requestedPixelRatio, Math.max(cappedPixelRatio, screenPixelRatio))
}
